import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Download } from "lucide-react";

function Paper({ doc }) {
  if (!doc) return null;
  const sig = doc.signature || {};
  return (
    <article className="bg-white text-slate-900 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.45)] border border-slate-200" data-testid="publication-agreement">
      <header className="px-8 sm:px-12 pt-10 pb-6 border-b border-slate-200">
        <div className="text-[11px] tracking-[0.35em] font-semibold text-slate-500">MERA STARTUP</div>
        <h2 className="mt-3 font-serif text-2xl sm:text-[1.7rem] leading-snug text-slate-950" style={{ fontFamily: "Georgia, 'Palatino Linotype', Palatino, serif" }}>
          {doc.title}
        </h2>
      </header>
      <div className="px-8 sm:px-12 py-8 space-y-6 text-[15px] leading-7" style={{ fontFamily: "Georgia, 'Palatino Linotype', Palatino, serif" }}>
        <p className="text-xs font-sans font-semibold tracking-[0.14em] text-red-700">IMPORTANT — PLEASE READ BEFORE SIGNING</p>
        <p>{doc.warning}</p>
        {(doc.sections || []).map((section) => (
          <section key={section.heading}>
            <h3 className="font-sans text-sm font-semibold tracking-wide text-slate-950 mb-2">{section.heading}</h3>
            {(section.paragraphs || []).map((p) => <p key={p.slice(0, 40)} className="mb-2">{p}</p>)}
            {section.bullets?.length ? (
              <ul className="list-disc pl-5 space-y-1">
                {section.bullets.map((b) => <li key={b.slice(0, 40)}>{b}</li>)}
              </ul>
            ) : null}
            {section.closing ? <p className="mt-2">{section.closing}</p> : null}
          </section>
        ))}
        <div className="border-t border-slate-200 pt-6 grid sm:grid-cols-[1fr_120px] gap-6 items-end">
          <dl className="space-y-3 text-sm">
            {[
              ["Merchant / Business Name", sig.store_name],
              ["Application Name", sig.app_name],
              ["Authorised Signatory Name", sig.signatory || "____________________"],
              ["Date and Time of Acceptance", sig.signed_at || "Recorded electronically"],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="font-sans text-[11px] uppercase tracking-[0.16em] text-slate-500">{label}</dt>
                <dd className="mt-0.5 border-b border-slate-300 pb-1">{value || "—"}</dd>
              </div>
            ))}
            <div>
              <dt className="font-sans text-[11px] uppercase tracking-[0.16em] text-slate-500">Electronic Signature</dt>
              <dd className="mt-2 text-slate-500 text-xs font-sans">Stamped by Mera Startup when this agreement is signed.</dd>
            </div>
          </dl>
          <div className="justify-self-center sm:justify-self-end w-[104px] h-[104px] rounded-full border-[3px] border-rose-800 text-rose-800 flex items-center justify-center text-center rotate-[-8deg]" data-testid="publication-stamp">
            <div className="font-sans text-[10px] font-extrabold tracking-[0.18em] leading-tight">
              MERA<br />STARTUP
            </div>
          </div>
        </div>
        <footer className="border-t border-slate-200 pt-5">
          <div className="font-sans text-xs font-semibold tracking-[0.16em] text-slate-500">MERA STARTUP — MERCHANT ACKNOWLEDGEMENT</div>
          <p className="mt-2 text-sm text-slate-600">{doc.acknowledgement}</p>
        </footer>
      </div>
    </article>
  );
}

async function savePdf(path, filename) {
  const { data } = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function StorePublication() {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [code, setCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/merchant/app-publication");
    setState(data);
  };
  useEffect(() => { load().catch((e) => toast.error(extractErr(e))); }, []);

  const sendCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/merchant/app-publication/otp");
      setOtpSent(true);
      toast.success(`Code sent to ${data.email}`);
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setBusy(false);
    }
  };

  const sign = async () => {
    setBusy(true);
    try {
      await api.post("/merchant/app-publication/sign", { code, signatory_name: name.trim(), accepted });
      toast.success("Agreement signed. Your app live request is with Mera Startup.");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading agreement…</div>;
  }

  const doc = state.signed ? state.request?.agreement : {
    ...state.preview,
    signature: { ...state.preview.signature, signatory: name.trim() },
  };

  return (
    <div className="space-y-5" data-testid="store-publication">
      <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-4 text-sm text-red-900" data-testid="publication-risk">
        <div className="font-semibold">We strongly recommend you to make your app live on your own Android Play Store account or iOS store account.</div>
        <p className="mt-2">
          Because publishing through Mera Startup&apos;s Console account creates a risk of suspension or termination for the account, and Mera Startup does not guarantee protection against that risk or accept responsibility for resulting losses.
        </p>
      </div>

      {!state.signed && !open && (
        <Button type="button" className="bg-red-700 hover:bg-red-800 h-auto whitespace-normal text-left py-3" data-testid="publication-understand" onClick={() => setOpen(true)}>
          I understand the risk and i still wanted to make it live in mera startup console account
        </Button>
      )}

      {(state.signed || open) && <Paper doc={doc} />}

      {state.signed && (
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" data-testid="publication-download" onClick={() => savePdf("/merchant/app-publication/pdf", "Mera-Startup-Publishing-Agreement.pdf").catch((e) => toast.error(extractErr(e)))}>
            <Download className="w-4 h-4 mr-1.5" /> Download agreement (PDF)
          </Button>
          <span className="text-xs text-slate-500">Signed {state.request?.agreement?.signature?.signed_at}. Status: {state.request?.status || "requested"}.</span>
        </div>
      )}

      {!state.signed && open && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="publication-sign">
          <div>
            <div className="text-sm font-semibold text-slate-900">Sign with your account email</div>
            <p className="text-xs text-slate-500 mt-1">A 6-digit code will be sent to {state.email || "your account email"}. The code confirms that you accept this agreement.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Authorised signatory name</label>
            <Input data-testid="publication-signatory" className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" />
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" className="mt-1" data-testid="publication-accept" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
            <span>I have read this agreement and I accept the risk of publishing through Mera Startup&apos;s developer account.</span>
          </label>
          {!otpSent ? (
            <Button type="button" data-testid="publication-send-otp" disabled={busy || name.trim().length < 2 || !accepted} onClick={sendCode} className="bg-slate-900 hover:bg-slate-800">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Email me the signing code
            </Button>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Email code</label>
                <Input data-testid="publication-otp" className="mt-1 font-mono w-36" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" inputMode="numeric" />
              </div>
              <Button type="button" data-testid="publication-sign-btn" disabled={busy || code.trim().length < 4} onClick={sign} className="bg-slate-900 hover:bg-slate-800">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Sign agreement
              </Button>
              <button type="button" className="text-xs text-slate-500 underline" onClick={sendCode} disabled={busy}>Resend code</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { savePdf };
